import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Typography, TextField, CircularProgress, Dialog, IconButton, Popover } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTranslation } from 'react-i18next';
import SEO from '../components/SEO/SEO';
import { uploadBase64ToImageKit, deleteImage } from '../services/images';
import { generateImage } from '../services/runware';
import { saveImageRecord, deleteImageRecord, getUserImages, type ImageDoc, updateImageFavorite } from '../services/gallery';
import { getOptimizedImageUrl } from '../lib/imagekit';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AddIcon from '@mui/icons-material/Add';
import ImageIcon from '@mui/icons-material/Image';
import CloseIcon from '@mui/icons-material/Close';
import { db, auth } from '../firebase/config';
import { doc, getDoc } from 'firebase/firestore';

const Gallery: React.FC = () => {
  const { t } = useTranslation();
  const [testText, setTestText] = useState('Un chat qui vole dans un ciel étoilé');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedImageUrl, setGeneratedImageUrl] = useState<string | null>(null);
  const [uploadedMeta, setUploadedMeta] = useState<{ url: string; fileId?: string; filePath?: string; name?: string } | null>(null);
  const [runwareDataUrl, setRunwareDataUrl] = useState<string | null>(null);
  const [firestoreDocId, setFirestoreDocId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [galleryItems, setGalleryItems] = useState<ImageDoc[]>([]);
  const [favoritesOnly, setFavoritesOnly] = useState<boolean>(false);
  const [previewItem, setPreviewItem] = useState<ImageDoc | null>(null);
  const [deletePopover, setDeletePopover] = useState<{ anchorEl: HTMLElement | null; item: ImageDoc | null }>({ anchorEl: null, item: null });
  const [dreamPopover, setDreamPopover] = useState<{ anchorEl: HTMLElement | null; item: ImageDoc | null }>({ anchorEl: null, item: null });
  const [dreamTexts, setDreamTexts] = useState<Record<string, string>>({});
  const [dreamErrors, setDreamErrors] = useState<Record<string, string>>({});
  const [dreamLoadingId, setDreamLoadingId] = useState<string | null>(null);

  const theme = useTheme();
  const isLgUp = useMediaQuery(theme.breakpoints.up('lg'));
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const isSmUp = useMediaQuery(theme.breakpoints.up('sm'));

  const visibleItems = useMemo(() => {
    if (favoritesOnly) {
      return galleryItems.filter((item) => item.favorite);
    }
    return galleryItems;
  }, [favoritesOnly, galleryItems]);

  const columns = useMemo(() => {
    if (isLgUp) return 4;
    if (isMdUp) return 3;
    if (isSmUp) return 2;
    return 1;
  }, [isLgUp, isMdUp, isSmUp]);

  const placeholderKeys = useMemo(() => {
    const cols = Math.max(columns, 1);
    const rowsWithImages = visibleItems.length > 0 ? Math.ceil(visibleItems.length / cols) : 0;
    const totalPlaceholders = cols * (rowsWithImages + 1) - visibleItems.length;
    const count = Math.max(totalPlaceholders, cols);
    return Array.from({ length: count }, (_, index) => index);
  }, [visibleItems.length, columns]);

  const testImageKitStorage = async () => {
    try {
      setError(null);
      console.log('Test ImageKit.io Storage...');
      
      const canvas = document.createElement('canvas');
      canvas.width = 100;
      canvas.height = 100;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        const gradient = ctx.createLinearGradient(0, 0, 100, 100);
        gradient.addColorStop(0, '#FF0000');
        gradient.addColorStop(1, '#0000FF');
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, 100, 100);
        
        ctx.fillStyle = 'white';
        ctx.font = '12px Arial';
        ctx.fillText('Test LunaNuva', 10, 50);
      }
      
      const blob = await new Promise<Blob>((resolve) => {
        canvas.toBlob((blob) => resolve(blob!), 'image/png');
      });
      
      const base64 = await new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.readAsDataURL(blob);
      });
      
      console.log('Envoi vers le serveur pour upload sécurisé...');
      const fileName = `lunanuva-test-${Date.now()}.png`;
      const uploadRes = await uploadBase64ToImageKit(base64, fileName, 'lunanuva/tests');
      console.log('Réponse du worker ImageKit:', uploadRes);
      const finalUrl = uploadRes?.url;
      if (!finalUrl) throw new Error('Réponse sans URL retournée par le serveur');
      setGeneratedImageUrl(finalUrl);
      setUploadedMeta({
        url: finalUrl,
        fileId: uploadRes?.data?.fileId,
        filePath: uploadRes?.filePath || uploadRes?.data?.filePath,
        name: uploadRes?.name || uploadRes?.data?.name,
      });
      console.log('ImageKit.io fonctionne ! URL:', finalUrl);
      
    } catch (error) {
      console.error('Erreur ImageKit Storage:', error);
      setError(`Erreur ImageKit Storage: ${error}`);
    }
  };

  const deleteImageFromImageKit = async () => {
    try {
      setError(null);
      if (!uploadedMeta?.url && !uploadedMeta?.fileId && !uploadedMeta?.filePath) {
        throw new Error("Aucune métadonnée d'image à supprimer");
      }
      const payload: any = {};
      if (uploadedMeta?.fileId) payload.fileId = uploadedMeta.fileId;
      else if (uploadedMeta?.filePath) payload.filePath = uploadedMeta.filePath;
      else if (uploadedMeta?.url) payload.url = uploadedMeta.url;

      const resJson = await deleteImage(payload);
      console.log('Suppression ImageKit:', resJson);

      if (firestoreDocId) {
        await deleteImageRecord(firestoreDocId);
      }

      setUploadedMeta(null);
      setGeneratedImageUrl(null);
      setFirestoreDocId(null);
      setRunwareDataUrl(null);
    } catch (err) {
      console.error('Erreur suppression ImageKit:', err);
      setError(`Erreur suppression: ${err}`);
    }
  };

  const testRunwareAPI = async (): Promise<string> => {
    try {
      setIsGenerating(true);
      setError(null);
      setRunwareDataUrl(null);
      setGeneratedImageUrl(null);
      setUploadedMeta(null);
      setFirestoreDocId(null);

      const result = await generateImage({
        prompt: testText,
        model: 'sourceful:2@2',
        width: 720,
        height: 1280,
        numberResults: 1,
        outputQuality: 85,
        /*steps: 20,*/
      });

      console.log('Réponse Runware (proxy worker):', result);
      let dataUrl = result.dataUrl || null;
      const d: any = result?.data;
      const guessB64 = d?.image_base64 
        || d?.images?.[0]?.b64_json 
        || d?.data?.[0]?.b64_json 
        || null;
      if (!dataUrl && guessB64) {
        dataUrl = `data:image/png;base64,${guessB64}`;
      }
      if (!dataUrl) {
        console.error('Payload Runware non exploitable:', d);
        throw new Error(`Runware a renvoyé une erreur (${result.status}). Détails: ${JSON.stringify(d)}`);
      }
      setRunwareDataUrl(dataUrl);
      return dataUrl;
    } catch (error) {
      console.error('Erreur Runware:', error);
      setError(`Erreur Runware: ${error}`);
      throw error;
    } finally {
      setIsGenerating(false);
    }
  };

  const saveGeneratedToImageKitAndFirestore = async (dataUrl?: string) => {
    try {
      setError(null);
      const payload = dataUrl || runwareDataUrl;
      if (!payload) {
        throw new Error("Aucune image Runware à enregistrer");
      }
      const fileName = `lunanuva-gen-${Date.now()}.png`;
      const uploadRes = await uploadBase64ToImageKit(payload, fileName, 'lunanuva/gallery');
      const finalUrl = uploadRes.url;
      setGeneratedImageUrl(finalUrl);
      setUploadedMeta({
        url: finalUrl,
        fileId: uploadRes?.data?.fileId,
        filePath: uploadRes?.filePath || uploadRes?.data?.filePath,
        name: uploadRes?.name || uploadRes?.data?.name,
      });

      const docId = await saveImageRecord({
        url: finalUrl,
        filePath: uploadRes?.filePath || uploadRes?.data?.filePath,
        fileId: uploadRes?.data?.fileId,
        name: uploadRes?.name || uploadRes?.data?.name,
        favorite: false,
        prompt: testText,
        dreamId: null,
        width: 720,
        height: 1280,
      });
      setFirestoreDocId(docId);
      setGalleryItems((prev) => [
        {
          id: docId,
          url: finalUrl,
          filePath: uploadRes?.filePath || uploadRes?.data?.filePath,
          fileId: uploadRes?.data?.fileId,
          name: uploadRes?.name || uploadRes?.data?.name,
          favorite: false,
          prompt: testText,
          width: 720,
          height: 1280,
          dreamId: null,
          createdAt: new Date().toISOString()
        },
        ...prev,
      ]);
    } catch (err) {
      console.error('Erreur sauvegarde ImageKit/Firestore:', err);
      setError(`Erreur sauvegarde: ${err}`);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const items = await getUserImages();
        setGalleryItems(items);
      } catch (e) {
        console.warn('Impossible de charger la galerie (utilisateur non connecté ?):', e);
      }
    })();
  }, []);

  const deleteFromGallery = async (item: ImageDoc) => {
    try {
      const payload: any = {};
      if (item.fileId) payload.fileId = item.fileId;
      else if (item.filePath) payload.filePath = item.filePath;
      else if (item.url) payload.url = item.url;
      await deleteImage(payload);
      await deleteImageRecord(item.id);
      setGalleryItems((prev) => prev.filter((it) => it.id !== item.id));
    } catch (e) {
      setError(`Erreur suppression: ${e}`);
    }
  };

  const toggleFavorite = async (item: ImageDoc) => {
    try {
      const next = !item.favorite;
      await updateImageFavorite(item.id, next);
      setGalleryItems((prev) => prev.map((it) => (it.id === item.id ? { ...it, favorite: next } : it)));
      setPreviewItem((prev) => (prev && prev.id === item.id ? { ...prev, favorite: next } : prev));
    } catch (err) {
      console.error('Erreur update favorite:', err);
      setError(`Erreur favoris: ${err}`);
    }
  };

  const openDreamPopover = (event: React.MouseEvent<HTMLElement>, item: ImageDoc) => {
    event.stopPropagation();
    setDreamPopover({ anchorEl: event.currentTarget, item });
  };

  const closeDreamPopover = () => {
    setDreamPopover({ anchorEl: null, item: null });
  };

  useEffect(() => {
    const dreamId = dreamPopover.item?.dreamId;
    if (!dreamId) return;
    if (dreamTexts[dreamId] || dreamErrors[dreamId]) return;

    let isActive = true;

    const fetchDream = async () => {
      setDreamLoadingId(dreamId);
      try {
        const user = auth.currentUser;
        if (!user) {
          if (isActive) {
            setDreamErrors((prev) => ({ ...prev, [dreamId]: 'not-authenticated' }));
          }
          return;
        }

        const dreamRef = doc(db, 'users', user.uid, 'dreams', dreamId);
        const snapshot = await getDoc(dreamRef);
        if (!snapshot.exists()) {
          if (isActive) {
            setDreamErrors((prev) => ({ ...prev, [dreamId]: 'not-found' }));
          }
          return;
        }

        const data = snapshot.data();
        const text = typeof data?.text === 'string' ? data.text : '';
        if (isActive) {
          setDreamTexts((prev) => ({ ...prev, [dreamId]: text }));
        }
      } catch (error) {
        if (isActive) {
          setDreamErrors((prev) => ({ ...prev, [dreamId]: 'fetch-error' }));
        }
      } finally {
        if (isActive) {
          setDreamLoadingId((current) => (current === dreamId ? null : current));
        }
      }
    };

    fetchDream();

    return () => {
      isActive = false;
    };
  }, [dreamPopover.item?.dreamId, dreamTexts, dreamErrors]);

  const handleToggleFavoritePreview = () => {
    if (!previewItem) return;
    toggleFavorite(previewItem);
  };

  const openDeletePopover = (event: React.MouseEvent<HTMLElement>, item: ImageDoc) => {
    event.stopPropagation();
    setDeletePopover({ anchorEl: event.currentTarget, item });
  };

  const closeDeletePopover = () => {
    setDeletePopover({ anchorEl: null, item: null });
  };

  const confirmDelete = async () => {
    if (!deletePopover.item) return;
    await deleteFromGallery(deletePopover.item);
    if (previewItem && deletePopover.item.id === previewItem.id) {
      setPreviewItem(null);
    }
    closeDeletePopover();
  };

  const activeDreamId = dreamPopover.item?.dreamId || null;
  const dreamPopoverText = activeDreamId ? dreamTexts[activeDreamId] : null;
  const dreamPopoverLoading = activeDreamId ? dreamLoadingId === activeDreamId : false;
  const dreamPopoverHasError = activeDreamId ? Boolean(dreamErrors[activeDreamId]) : false;
  const dreamFallback = dreamPopover.item?.prompt?.trim() || t('gallery.controls.dreamPopoverEmpty');
  const dreamDisplayText = (() => {
    if (dreamPopoverLoading) return '';
    if (!dreamPopover.item) return t('gallery.controls.dreamPopoverEmpty');
    if (activeDreamId) {
      if (dreamPopoverText && dreamPopoverText.trim()) return dreamPopoverText.trim();
      if (dreamPopoverHasError) return t('gallery.controls.dreamPopoverEmpty');
      return dreamFallback;
    }
    return dreamFallback;
  })();

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      background: 'var(--Pages-background1)',
      display: 'flex',
      flexDirection: 'column'
      }}>
      <SEO
        title={t('seo.gallery.title')}
        description={t('seo.gallery.description')}
        keywords={t('seo.gallery.keywords')}
      />
      
      <Box sx={{ 
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 0.5,
        mt: { xs: 10, sm: 10, md: 10},
        mb: { xs: 3, sm: 3, md: 0 }
        }}>
        <Box sx={{ 
          display: 'grid',
          gridTemplateColumns: 'auto auto',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 0,
          mt: 0,
          width: '100%'
        }}>
          <Box 
            component="img"
            src="/assets/home/newdream.png"
            alt="Gallery"
            sx={{
              maxWidth: { xs: '200px', sm: '350px', md: '500px'},
              width: '100%',
              height: 'auto',
              display: 'block'
            }}
          />
          
          <Box sx={{
            fontFamily: '"Amatic SC", cursive',
            fontSize: { xs: '2.5rem', sm: '4rem', md: '5rem' },
            fontWeight: { xs: '600', sm: '400', md: '400' },
            color: 'var(--ConnectButton)',            
            lineHeight: 1.1,
            whiteSpace: 'nowrap',
            justifySelf: 'start',
            marginLeft: { xs: '-75px', sm: '-100px', md: '-110px' },
            zIndex: 2
          }}>
            <Box component="span" sx={{ 
              display: 'block', 
              transform: 'rotate(-3deg)',
              transformOrigin: 'left center',
              ml: 10,
              mb: -1
            }}>
              {t('gallery.headerSubtitle').split(' ')[0]}
            </Box>
            
            <Box component="span" sx={{ 
              display: 'block', 
              transform: 'rotate(3deg)',
              transformOrigin: 'left center',
              ml: 5,
              mb: 0
            }}>
              {t('gallery.headerSubtitle').split(' ')[1]} {t('gallery.headerSubtitle').split(' ')[2]} {t('gallery.headerSubtitle').split(' ')[3]}
            </Box>
            
            <Box component="span" sx={{ 
              display: 'block', 
              transform: 'rotate(-2deg)',
              transformOrigin: 'left center',
              ml: 10
            }}>
              {t('gallery.headerSubtitle').split(' ')[4]} {t('gallery.headerSubtitle').split(' ')[5]} {t('gallery.headerSubtitle').split(' ')[6]}
            </Box>
          </Box>
        </Box>
      </Box>
      
      
{/* ------------------------Content------- */}
<Box sx={{ width: '100%', flex: 1, display: 'flex', justifyContent: 'center', px: { xs: 2, md: 4 }, pb: { xs: 4, md: 6 },mt: 4 }}>
        <Box sx={{ width: '100%', maxWidth: 1400, display: 'flex', flexDirection: { xs: 'column', sm: 'row', md: 'row' }, gap: { xs: 3, md: 4 } }}>
          
          {/* Sidebar----------------------------------------------- */}
          <Box sx={{ width: { xs: '100%',sm: 200, md: 200 }, flexShrink: 0, backgroundColor: 'rgba(255, 255, 255, 0.00)', p: { xs: 1.5, sm: 1, md: 1 }, display: 'flex', flexDirection: 'column', gap: 3, borderRadius: .5 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Button
                variant="sidebar"
                startIcon={<ImageIcon />}
                sx={{
                  justifyContent: 'flex-start',
                  px: 2,
                  py: 1.5,
                  borderRadius: 0.2,
                  background: favoritesOnly ? 'transparent' : 'var(--fd-gridBLOG)',
                  color: favoritesOnly ? 'rgba(255, 255, 255, 0.9)' : 'var(--ConnectButton)',
                  fontWeight: 500,
                  '&:hover': {
                    background: favoritesOnly ? 'rgba(255,255,255,0.25)' : 'var(--dark-greenv2)'
                  }
                }}
                onClick={() => setFavoritesOnly(false)}
              >
                {t('gallery.controls.allImages')}
              </Button>

              <Button
                variant="sidebar"
                startIcon={<FavoriteIcon />}
                sx={{
                  justifyContent: 'flex-start',
                  px: 2,
                  py: 1.5,
                  borderRadius: 0.2,
                  background: favoritesOnly ? 'var(--fd-gridBLOG)' : 'transparent',
                  color: favoritesOnly ? 'var(--ConnectButton)' : 'rgba(255,255,255,0.9)',
                  fontWeight: 500,
                  '&:hover': {
                    background: favoritesOnly ? 'var(--dark-greenv2)' : 'rgba(255,255,255,0.25)'
                  }
                }}
                onClick={() => setFavoritesOnly(true)}
              >
                {t('gallery.controls.favorites')}
              </Button>
            </Box>
          </Box>

          {/* Main content---------------------------------------------------------------- */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: 'repeat(1, minmax(0, 1fr))',
                  sm: 'repeat(2, minmax(0, 1fr))',
                  md: 'repeat(3, minmax(0, 1fr))',
                  lg: 'repeat(4, minmax(0, 1fr))',
                },
                gap: 1,
              }}
            >
              {visibleItems.map((item) => (
                <Box
                  key={item.id}
                  className="group"
                  sx={{
                    position: 'relative',
                    borderRadius: 0.3,
                    overflow: 'hidden',
                    cursor: 'pointer',
                    aspectRatio: '1 / 1',
                    boxShadow: '0 8px 16px rgba(0,0,0,0.02)',
                    '&:hover .overlay': { opacity: 1 },
                  }}
                >
                  <img
                    onClick={() => setPreviewItem(item)}
                    src={getOptimizedImageUrl(item.url, { width: 500, height: 500, quality: 80, format: 'webp', crop: 'maintain_ratio' })}
                    alt={item.name || 'generated'}
                    style={{
                      width: '100%',
                      height: '100%',
                      display: 'block',
                      objectFit: 'cover',
                    }}
                    loading="lazy"
                  />

                  <Box
                    className="overlay"
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      opacity: 0,
                      transition: 'opacity 0.2s ease',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      p: 1,
                      pointerEvents: 'none',
                    }}
                  >
                    <Box />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', pointerEvents: 'auto' }}>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleFavorite(item);
                          }}
                          sx={{
                            width: 32,
                            height: 32,
                            backgroundColor: 'var(--dark-blue)',
                            color: item.favorite ? 'var(--red)' : 'white',
                            '&:hover': { backgroundColor: 'rgba(0, 150, 219, 0.9)' },
                          }}
                          title={item.favorite ? t('gallery.controls.tooltipRemoveFavorite') : t('gallery.controls.tooltipAddFavorite')}
                        >
                          {item.favorite ? <FavoriteIcon fontSize="small" /> : <FavoriteBorderIcon fontSize="small" />}
                        </IconButton>

                        <IconButton
                          size="small"
                          onClick={(e) => openDreamPopover(e, item)}
                          sx={{
                            width: 32,
                            height: 32,
                            backgroundColor: 'var(--dark-blue)',
                            color: 'white',
                            '&:hover': { backgroundColor: 'rgba(0, 150, 219, 0.9)' },
                          }}
                          title={t('gallery.controls.tooltipShowDream')}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Box>

                      <IconButton
                        size="small"
                        onClick={(e) => openDeletePopover(e, item)}
                        sx={{
                          width: 32,
                          height: 32,
                          backgroundColor: 'var(--dark-blue)',
                          color: 'white',
                          '&:hover': { backgroundColor: 'rgba(0, 150, 219, 0.9)' },
                        }}
                        title={t('gallery.controls.tooltipDelete')}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                </Box>
              ))}

              {placeholderKeys.map((key) => (
                <Box
                  key={`placeholder-${key}`}
                  sx={{
                    borderRadius: .2,
                    aspectRatio: '1 / 1',
                    background: 'linear-gradient(135deg, rgba(78, 154, 166, 0.22)0%, rgba(120, 127, 165, 0.29) 100%)',
                    //border: '1px solid rgba(255,255,255,0.08)',
                    //boxShadow: 'inset -5px -5px 22px rgba(4, 7, 12, 0.92)',
                    opacity: 0.35,
                  }}
                />
              ))}
            </Box>

            {visibleItems.length === 0 && (
              <Typography variant="body2" sx={{ mt: 1, color: 'rgba(255,255,255,0.7)' }}>
                Aucune image pour le moment. Utilise les filtres ou génère une nouvelle image pour remplir la galerie.
              </Typography>
            )}

            {error && (
              <Box sx={{ backgroundColor: 'rgba(255, 107, 107, 0.1)', border: '1px solid rgba(255, 107, 107, 0.3)', borderRadius: 2, p: 2 }}>
                <Typography sx={{ color: '#ff6b6b' }}>❌ {error}</Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* Aperçu plein écran (lightbox) */}
      <Dialog
        open={!!previewItem}
        onClose={() => setPreviewItem(null)}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: { xs: .3, sm: .5, md: .8, lg: 1 },
            backgroundColor: 'transparent',
            boxShadow: 'none',
            maxWidth: 'min(1024px, 100vw)',
            m: { xs: 1, sm: 2 },
          },
        }}
      >
        {previewItem && (
          <Box
            sx={{
              position: 'relative',
              backgroundColor: 'rgba(0, 0, 0, 0.95)',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'center', md: 'stretch' },
              gap: { xs: 2, sm: 2.5, md: 3 },
              p: { xs: 2, sm: 2.5, md: 3 },
            }}
          >
            <Box
              sx={{
                order: { xs: 1, md: 2 },
                width: { xs: '100%', md: 'auto' },
                minWidth: { md: 72 },
                display: 'flex',
                flexDirection: { xs: 'row', md: 'column' },
                justifyContent: { xs: 'flex-end', md: 'flex-start' },
                alignItems: { xs: 'center', md: 'flex-end' },
                gap: 1,
                pr: { xs: 0.5, sm: 1, md: 0 },
              }}
            >
              <IconButton
                onClick={() => setPreviewItem(null)}
                sx={{
                  color: 'white',
                  backgroundColor: 'var(--home-subtitle)',
                  '&:hover': { backgroundColor: 'var(--primary-color)' },
                }}
                aria-label={t('common.close')}
              >
                <CloseIcon />
              </IconButton>

              <IconButton
                onClick={handleToggleFavoritePreview}
                sx={{
                  width: 40,
                  height: 40,
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  color: previewItem.favorite ? 'var(--red)' : 'white',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.35)' },
                }}
                aria-label={previewItem.favorite ? t('gallery.controls.tooltipRemoveFavorite') : t('gallery.controls.tooltipAddFavorite')}
              >
                {previewItem.favorite ? <FavoriteIcon fontSize="small" /> : <FavoriteBorderIcon fontSize="small" />}
              </IconButton>

              <IconButton
                onClick={(e) => previewItem && openDreamPopover(e, previewItem)}
                sx={{
                  width: 40,
                  height: 40,
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.35)' },
                }}
                aria-label={t('gallery.controls.tooltipShowDream')}
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>

              <IconButton
                onClick={(e) => previewItem && openDeletePopover(e, previewItem)}
                sx={{
                  width: 40,
                  height: 40,
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.35)' },
                }}
                aria-label={t('gallery.controls.tooltipDelete')}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>

            <Box
              sx={{
                order: { xs: 2, md: 1 },
                flexGrow: 1,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                pb: { xs: 2, sm: 2, md: 0 },
              }}
            >
              <img
                src={previewItem.url}
                alt={previewItem.name || 'preview'}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  maxWidth: '1024px',
                  maxHeight: '100vh',
                }}
              />
            </Box>

            <Box
              sx={{
                order: 3,
                display: { xs: 'block', md: 'none' },
                width: '100%',
                height: 48,
              }}
            />
          </Box>
        )}
      </Dialog>

      <Popover
        open={Boolean(dreamPopover.anchorEl)}
        anchorEl={dreamPopover.anchorEl}
        onClose={closeDreamPopover}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        disableRestoreFocus
        PaperProps={{
          sx: {
            background: 'var(--Pages-background)',
            borderRadius: 0.2,
            boxShadow: '0 20px 40px rgba(0,0,0,0.35)',
            maxWidth: 320,
            p: 2,
          },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
            <Typography variant="subtitle1" sx={{ fontFamily: 'Comfortaa', color: 'white', fontWeight: 600 }}>
              {t('gallery.controls.dreamPopoverTitle')}
            </Typography>
            <IconButton
              onClick={closeDreamPopover}
              size="small"
              sx={{
                color: 'rgba(255,255,255,0.8)',
                '&:hover': { color: 'rgba(255,255,255,1)' },
              }}
              aria-label={t('common.close')}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          {dreamPopoverLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={18} sx={{ color: 'rgba(255,255,255,0.8)' }} />
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)' }}>
                {t('common.loading')}
              </Typography>
            </Box>
          ) : (
            <Typography
              variant="body2"
              sx={{ color: 'rgba(255,255,255,0.85)', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}
            >
              {dreamDisplayText}
            </Typography>
          )}
        </Box>
      </Popover>

      <Popover
        open={Boolean(deletePopover.anchorEl)}
        anchorEl={deletePopover.anchorEl}
        onClose={closeDeletePopover}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        disableRestoreFocus
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(12, 22, 48, 0.98)',
            borderRadius: 0.5,
            boxShadow: '0 20px 40px rgba(0,0,0,0.35)',
          },
        }}
      >
        <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 260 }}>
          <Box>
            <Typography variant="subtitle1" sx={{ fontFamily: 'Comfortaa', color: 'white', fontWeight: 600 }}>
              {t('gallery.controls.deleteConfirmTitle')}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5, color: 'rgba(255,255,255,0.75)' }}>
              {t('gallery.controls.deleteConfirmDescription')}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5, color: 'rgba(255,255,255,0.6)' }}>
              {t('gallery.controls.deleteConfirmDreamRetention')}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button
              onClick={confirmDelete}
              sx={{
                px: 2,
                py: 1,
                borderRadius: 0.2,
                background: 'var(--fd-gridBLOG)',
                color: 'var(--ConnectButton)',
                fontWeight: 500,
                textTransform: 'none',
                '&:hover': { background: 'var(--dark-greenv2)' },
              }}
            >
              {t('gallery.controls.deleteButton')}
            </Button>

            <Button
              onClick={closeDeletePopover}
              sx={{
                px: 2,
                py: 1,
                borderRadius: 0.2,
                background: 'transparent',
                color: 'rgba(255,255,255,0.9)',
                fontWeight: 500,
                textTransform: 'none',
                '&:hover': { background: 'rgba(255,255,255,0.25)' },
              }}
            >
              {t('gallery.controls.cancelButton')}
            </Button>
          </Box>
        </Box>
      </Popover>

    </Box>
  );
};

export default Gallery;
